/**
 * Internal Assignment-Notification Master Switch API Contract
 *
 * POST /internal/platform-data-sync/assignment-notifications  — set the switch
 * GET  /internal/platform-data-sync/assignment-notifications  — read merged state + audit
 *
 * API-key-gated (`INTERNAL_API_KEY`). The switch decides whether the assignment
 * sync emits `assignment_created` / `assignment_updated` events at all — the
 * events that become bell-feed entries and OneSignal pushes. Turning it off
 * silences assignment notifications WITHOUT stopping the sync: assignment rows
 * keep landing in the database, only the notification fan-out stops.
 *
 * Storage is a `SystemConfiguration` row (`category: 'PLATFORM_SYNC_SETTINGS'`,
 * `key: 'ASSIGNMENT_NOTIFICATIONS'`, `value: { enabled: boolean }`). `school_id`
 * is OPTIONAL: omit it to write/read the GLOBAL (`school_id IS NULL`) row;
 * supply it to write/read that school's override, which takes precedence.
 *
 * DEFAULT IS **SUPPRESS** — the switch fails closed, like Autopilot. Only a
 * literal `enabled: true` at the effective level emits; `false`, a missing key,
 * a malformed value or no row at either level suppresses. A read failure at the
 * emit site also suppresses (fails closed).
 *
 * Audit trail: each write records `{ reason, changedBy, changedAt }` in the
 * row's `metadata` JSON column, kept separate from the functional
 * `value.enabled` the sync reads. The GET surfaces it back.
 */

import { z } from 'zod'

/**
 * Audit record for a single switch flip, stored in `SystemConfiguration.metadata`.
 */
export const AssignmentNotificationsAuditSchema = z.object({
  reason: z.string().nullable().describe('Free-text reason supplied by the caller, if any'),
  changedBy: z.string().describe("Caller identity (defaults to 'internal-api')"),
  changedAt: z.string().describe('ISO timestamp of the flip')
})

export type AssignmentNotificationsAudit = z.infer<typeof AssignmentNotificationsAuditSchema>

/**
 * Which configuration level supplied the effective `enabled` value.
 *  - `school`: a per-school row exists (overrides global)
 *  - `system`: only the global (`school_id IS NULL`) row exists
 *  - `none`:   neither exists → effective `enabled` defaults to FALSE (suppress)
 */
export const AssignmentNotificationsSourceLevelSchema = z.enum(['school', 'system', 'none'])

export type AssignmentNotificationsSourceLevel = z.infer<typeof AssignmentNotificationsSourceLevelSchema>

/**
 * POST request body. `school_id` is OPTIONAL — omit for the global switch.
 */
export const SetAssignmentNotificationsRequestSchema = z.object({
  school_id: z
    .string()
    .min(1)
    .optional()
    .describe('OPTIONAL school public ID (CUID) or slug; omit to write the global switch'),
  enabled: z.boolean().describe('New switch state — false suppresses assignment notification emission'),
  reason: z.string().max(2000).optional().describe('Optional audit reason for the change'),
  changed_by: z
    .string()
    .max(200)
    .optional()
    .describe("Optional caller identity for the audit trail (defaults to 'internal-api')")
})

export type SetAssignmentNotificationsRequest = z.infer<typeof SetAssignmentNotificationsRequestSchema>

/**
 * The merged effective switch state. `enabled` mirrors the emit-site precedence
 * (per-school over global; neither present → false / SUPPRESS).
 */
export const AssignmentNotificationsStateSchema = z.object({
  schoolId: z.string().nullable().describe("School's public ID, or null when the read/write was global"),
  enabled: z
    .boolean()
    .describe('Merged effective state — true only when the effective level holds enabled: true; false suppresses'),
  source: AssignmentNotificationsSourceLevelSchema.describe('Which level supplied the effective value'),
  systemEnabled: z.boolean().nullable().describe('Global row enabled (null if no global row)'),
  schoolEnabled: z.boolean().nullable().describe('Per-school enabled (null if no per-school row, or none requested)'),
  audit: AssignmentNotificationsAuditSchema.nullable().describe(
    'Audit record from the level that supplied the effective value (null if never set)'
  )
})

export type AssignmentNotificationsState = z.infer<typeof AssignmentNotificationsStateSchema>

/**
 * POST response.
 */
export const SetAssignmentNotificationsResponseSchema = z.object({
  message: z.string(),
  data: AssignmentNotificationsStateSchema
})

export type SetAssignmentNotificationsResponse = z.infer<typeof SetAssignmentNotificationsResponseSchema>

/**
 * GET request (query). `school_id` is OPTIONAL — omit to read the global switch.
 */
export const GetAssignmentNotificationsRequestSchema = z.object({
  school_id: z
    .string()
    .min(1)
    .optional()
    .describe('OPTIONAL school public ID (CUID) or slug; omit to read the global switch')
})

export type GetAssignmentNotificationsRequest = z.infer<typeof GetAssignmentNotificationsRequestSchema>

/**
 * GET response.
 */
export const GetAssignmentNotificationsResponseSchema = z.object({
  message: z.string(),
  data: AssignmentNotificationsStateSchema
})

export type GetAssignmentNotificationsResponse = z.infer<typeof GetAssignmentNotificationsResponseSchema>
